// exclude-from-index
import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import { StyledButton, StyledSingleDateSelect } from './SingleDateSelect.styles';

import { Select } from '../Select';
import { Spacer } from '../Spacer';
import { SVGIcon } from '../../SVGIcon';

// class SingleDateSelect extends PureComponent {
//   constructor(props) {
//     super(props);

//     // Parse year, month, day if defaultDate is provided
//     const splitDate = props.defaultDate.split('-');
//     const year = props.defaultDate && splitDate[0] ? splitDate[0].toString() : '';
//     const month = props.defaultDate && splitDate[1] ? parseInt(splitDate[1], 10).toString() : '';
//     const day = props.defaultDate && splitDate[2] ? parseInt(splitDate[2], 10).toString() : '';

//     this.state = {
//       dayData: year && month ? SingleDateSelect.getDayData(SingleDateSelect.daysInMonth(year, month)) : [],
//       dayValue: day,
//       disableDay: !month,
//       disableMonth: !year,
//       disableYear: props.disabled,
//       monthData: SingleDateSelect.getMonthData(),
//       monthValue: month,
//       yearData: SingleDateSelect.getYearData(props.startYear, props.endYear),
//       yearValue: year
//     };
//   }
// }

function SingleDateSelect(props) {
  const { disabled, error, errorText, name, className, stack, defaultDate, onChange, startYear, endYear } = props;

  /**
   * @description - Returns the proper number of days for a selected month (including leap years)
   * @param {number} year - The year of target date
   * @param {number} month - The month of target date
   * @return {number} - 28
   */
  const daysInMonth = (year, month) => new Date(year, month, 0).getDate();

  /**
   * @description - Creates an array of day objects {label, value} to be used with select components
   * @param {number} numOfDays - The number of days to create
   * @return {array} - [{label: '1', value: '1'}]
   */
  const getDayData = (numOfDays = 0) => {
    const dayData = [{ label: '', value: '' }];

    for (let i = 0; i < numOfDays; i += 1) {
      const label = (parseInt(i, 10) + 1).toString();
      const value = (i + 1).toString();

      dayData.push({ label, value });
    }

    return dayData;
  };

  /**
   * @description - Creates an array of month objects {label, value} to be used with select components
   * @return {array} - [{label: 'January', value: '1'}]
   */
  const getMonthData = () => {
    const monthData = [{ label: '', value: '' }];
    const monthLabels = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December'
    ];

    for (let i = 0; i < 12; i += 1) {
      const label = monthLabels[i];
      const value = (i + 1).toString();
      monthData.push({ label, value });
    }

    return monthData;
  };

  /**
   * @description - Creates an array of year objects {label, value} to be used with select components
   * @return {array} - [{label: '2019', value: '2019'}]
   */
  const getYearData = (newStartYear, newEndYear) => {
    const yearData = [{ label: '', value: '' }];
    const yearRange = newEndYear - newStartYear + 1;
    const firstDate = newEndYear;

    for (let i = 0; i < yearRange; i += 1) {
      const year = (firstDate - i).toString();

      yearData.push({ label: year, value: year });
    }

    return yearData;
  };

  const {
    current: { splitDate }
  } = useRef(defaultDate.split('-'));

  const {
    current: { year, month, day }
  } = useRef({
    year: defaultDate && splitDate[0] ? splitDate[0].toString() : '',
    month: defaultDate && splitDate[1] ? parseInt(splitDate[1], 10).toString() : '',
    day: defaultDate && splitDate[2] ? parseInt(splitDate[2], 10).toString() : ''
  });

  const [dayData, setDayData] = useState(year && month ? getDayData(daysInMonth(year, month)) : []);
  const [dayValue, setDayValue] = useState(day);
  const [disableDay, setDisableDay] = useState(!month);
  const [disableMonth, setDisableMonth] = useState(!year);
  const [monthValue, setMonthValue] = useState(month);
  const [yearValue, setYearValue] = useState(year);

  const {
    current: { disableYear, monthData, yearData }
  } = useRef({
    disableYear: disabled,
    monthData: getMonthData(),
    yearData: getYearData(startYear, endYear)
  });

  /**
   * @description - Handles clearing all the select values
   */
  const handleClearDate = () => {
    onChange('');

    setDayValue('');
    setDisableDay(true);
    setDisableMonth(true);
    setMonthValue('');
    setYearValue('');
  };

  /**
   * @description - Handles updating state when day select has changed
   * @param {object} evt - Event object
   */
  const handleDayChange = (evt) => {
    const value = evt.target.value.toString();
    const padMonth = monthValue.length > 1 ? monthValue : `0${monthValue}`;
    const padDay = value.length > 1 ? value : `0${value}`;
    const dateString = `${yearValue}-${padMonth}-${padDay}`;

    setDayValue(value);

    onChange(dateString);
  };

  /**
   * @description - Handles updating state when month select has changed
   * @param {object} evt - Event object
   */
  const handleMonthChange = (evt) => {
    const value = parseInt(evt.target.value, 10);

    if (dayValue) {
      onChange('');
    }

    setDayData(SingleDateSelect.getDayData(SingleDateSelect.daysInMonth(yearValue, value)));
    setDayValue('');
    setDisableDay(value < 1);
    setMonthValue(value.toString());
  };

  /**
   * @description - Handles updating state when year select has changed
   * @param {object} evt - Event object
   */
  const handleYearChange = (evt) => {
    const value = evt.target.value.toString();

    if (dayValue) {
      onChange('');
    }

    setDayValue('');
    setDisableDay(true);
    setDisableMonth(!value);
    setMonthValue('');
    setYearValue(value);
  };

  return (
    <FormControl error={error} fullWidth>
      <StyledSingleDateSelect stack={stack} className={className} disabled={disabled}>
        <div>
          <Select
            autoWidth={!stack}
            disabled={disabled || disableYear}
            error={!yearValue && !disabled && error}
            id={`${name}-year`}
            className="single-date-year-select"
            label="Year"
            name={`${name}Year`}
            onChange={handleYearChange}
            options={yearData}
            value={yearValue}
            native
            data-cy="sds-year"
          />
          {stack && <Spacer size="sm" />}
        </div>
        <div>
          <Select
            autoWidth={!stack}
            disabled={disabled || disableMonth}
            error={!monthValue && !disabled && !disableMonth && error}
            id={`${name}-month`}
            className="single-date-month-select"
            label="Month"
            name={`${name}Month`}
            onChange={handleMonthChange}
            options={monthData}
            value={monthValue}
            native
            data-cy="sds-month"
          />
          {stack && <Spacer size="sm" />}
        </div>
        <div>
          <Select
            autoWidth={!stack}
            disabled={disabled || disableDay}
            error={!dayValue && !disabled && !disableDay && error}
            id={`${name}-day`}
            className="single-date-day-select"
            label="Day"
            name={`${name}Day`}
            onChange={handleDayChange}
            options={dayData}
            value={dayValue}
            native
            data-cy="sds-day"
          />
          {stack && <Spacer size="sm" />}
        </div>
        {error && stack && <FormHelperText>{errorText}</FormHelperText>}
        {stack ? (
          <>
            <Spacer size="sm" />
            <StyledButton pill onClick={handleClearDate}>
              Clear Date <SVGIcon selectedIcon="clear-search" iconColor="#FFFFFF" onClick={handleClearDate} />
            </StyledButton>
            <Spacer size="sm" />
          </>
        ) : (
          <SVGIcon selectedIcon="clear-search" onClick={handleClearDate} />
        )}
      </StyledSingleDateSelect>
      {error && !stack && <FormHelperText> {errorText}</FormHelperText>}
    </FormControl>
  );
}

SingleDateSelect.propTypes = {
  error: PropTypes.bool,
  errorText: PropTypes.string,
  /**
   * Preset date
   */
  defaultDate: PropTypes.string,
  /**
   * disables the use of this component, and updates the styles
   */
  disabled: PropTypes.bool,
  /**
   * name attribute applied to select elements
   */
  name: PropTypes.string,
  /**
   *  callback function when year, month, day has changed. Returns number value.
   */
  onChange: PropTypes.func.isRequired,
  /**
   *  Start of the year range eg: 1920
   */
  startYear: PropTypes.number,
  /**
   *  End of the year range eg: 2020
   */
  endYear: PropTypes.number,
  /**
   * Classname for styling purposes
   */
  className: PropTypes.string,
  /**
   * sets the width to 100% and stacks the selects
   * */
  stack: PropTypes.bool
};

SingleDateSelect.defaultProps = {
  defaultDate: '',
  disabled: false,
  error: false,
  errorText: 'Please select a year, month and day',
  name: undefined,
  startYear: new Date().getFullYear() - 100,
  endYear: new Date().getFullYear(),
  className: '',
  stack: false
};

export default SingleDateSelect;
